import type { FC } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Typography } from '@mui/material'
import MarkChatUnreadOutlinedIcon from '@mui/icons-material/MarkChatUnreadOutlined'
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined'

import { HOME_CONTENT } from '../defaults'
import {
  cardSectionContainerSx,
  cardSectionTitleSx,
  cardSectionSubtitleSx,
  cardSectionCardsContainerSx,
  cardSx,
  cardTitleSx,
} from '../styles'

export const NotificationsSection: FC = () => (
  <Box component="section" sx={cardSectionContainerSx}>
    <Typography component="h2" sx={cardSectionTitleSx}>
      {HOME_CONTENT.notifications.title}
    </Typography>
    <Typography sx={cardSectionSubtitleSx}>{HOME_CONTENT.notifications.description}</Typography>

    <Box sx={cardSectionCardsContainerSx(960)}>
      <Box sx={cardSx}>
        <Typography component="h3" sx={cardTitleSx}>
          {HOME_CONTENT.notifications.cards[0].title}
        </Typography>
        <Typography sx={cardSubtitleSx}>{HOME_CONTENT.notifications.cards[0].body}</Typography>
        <Box sx={iconContainerSx} role="img" aria-label="Sound alert speaker">
          <VolumeUpOutlinedIcon sx={speakerIconSx} aria-hidden="true" />
        </Box>
      </Box>

      <Box sx={cardSx}>
        <Typography component="h3" sx={cardTitleSx}>
          {HOME_CONTENT.notifications.cards[1].title}
        </Typography>
        <Typography sx={cardSubtitleSx}>{HOME_CONTENT.notifications.cards[1].body}</Typography>
        <Box sx={iconContainerSx} role="img" aria-label="Browser push notification message">
          <MarkChatUnreadOutlinedIcon sx={pushIconSx} aria-hidden="true" />
        </Box>
      </Box>
    </Box>
  </Box>
)

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const cardSubtitleSx: SxProps<Theme> = {
  fontSize: '0.95rem',
  lineHeight: 1.6,
  color: 'text.secondary',
}

const iconContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  mt: 3,
}

const pushIconSx: SxProps<Theme> = {
  fontSize: 48,
  color: 'info.main',
  opacity: 0.7,
  '@media (prefers-reduced-motion: no-preference)': {
    '@keyframes pushPop': {
      '0%': { transform: 'scale(1)', opacity: 0.7 },
      '12%': { transform: 'scale(1.15)', opacity: 1 },
      '24%': { transform: 'scale(1)', opacity: 0.7 },
      '100%': { transform: 'scale(1)', opacity: 0.7 },
    },
    animation: 'pushPop 2.5s ease-in-out infinite',
  },
}

const speakerIconSx: SxProps<Theme> = {
  fontSize: 48,
  color: 'warning.light',
  opacity: 0.7,
  '@media (prefers-reduced-motion: no-preference)': {
    '@keyframes speakerBurst': {
      '0%': { transform: 'scale(1)', opacity: 0.7 },
      '8%': { transform: 'scale(1.12)', opacity: 0.95 },
      '16%': { transform: 'scale(1)', opacity: 0.7 },
      '24%': { transform: 'scale(1.12)', opacity: 0.95 },
      '32%': { transform: 'scale(1)', opacity: 0.7 },
      '40%': { transform: 'scale(1.1)', opacity: 0.9 },
      '48%': { transform: 'scale(1)', opacity: 0.7 },
      '100%': { transform: 'scale(1)', opacity: 0.7 },
    },
    animation: 'speakerBurst 4s ease-in-out infinite',
  },
}
